import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useAuth } from "@clerk/clerk-react";
import { ReconciliationService } from "@/api";
import type {
  reconciliation_DebtorAccountAggregatesResponse,
  reconciliation_DebtorAccountListResponse,
  reconciliation_DebtorAccountsSummaryResponse,
} from "@/api";

// FRESHNESS POLICY for the reconciliation money queries.
//
// The app-wide QueryClient (main.tsx) sets `refetchOnWindowFocus: false`. That
// default stays — it is a deliberate choice for the rest of the app — so every
// money query that an operator can act on opts IN explicitly, right here at the
// call site. Without the opt-in a `staleTime` window is a hard floor with no
// mechanism that ever ends it early: mutation invalidation only reaches the tab
// and the QueryClient that issued the write, so a second tab, a cash-in import,
// or a correction made by anyone else leaves the numbers wrong until a manual
// reload the operator has no reason to think is necessary.
//
// The same opt-in is on the debtor-detail money reads: `useDebtorAccountDetail`,
// `useDebtorLedger` and `useDebtorLedgerAll`.
//
// #1338 extended this to the rest of the reconciliation money namespace, and
// made every hook in it state its policy instead of inheriting one. That is
// the rule now: a money query declares `staleTime` AND `refetchOnWindowFocus`
// at its own call site, with a comment saying why, even when the value it
// declares equals the app default. An inherited default and a deliberate
// choice are indistinguishable to the next reader, and #987 is what that costs
// — two hooks carried comments describing a focus refetch nobody had turned on.
//
// Focus refetch is ON: `useDebtorPayments`, `usePaymentApplicationsByInvoice`,
// `useDebtorMonthlySeries`, `useReviewQueueList`, and the three
// `useSubsidyMatching` reads. Each is bounded — by one debtor, one invoice, the
// visible page, or the unapplied working set — so a tab-return costs a read
// that does not grow with the org's history.
//
// Focus refetch is OFF, deliberately, in exactly two places, for two different
// reasons that should not be collapsed into one:
//   - `useOrgPaymentRegister` — LOAD. Org-wide and unbounded; List + Count +
//     Sum over every non-deleted `payments` row plus the refund arm, on a
//     throttled 0.5 CPU tier, unmeasured at production scale. Note #1042
//     measures the debtor-accounts list path and the KPI summary, NOT this
//     endpoint, so turning it on needs its own measurement.
//   - `useReviewQueueCandidates` — CORRECTNESS. The operator confirms the card
//     at index 0 by keyboard; a refetch on tab-return can change which debtor
//     sits there.
//
// Both are covered by `invalidateMoneyViews` in-tab and cross-tab regardless,
// so "off" means "no extra refetch trigger", not "never refreshed".

export interface DebtorAccountsListFilters {
  hasCredit?: boolean;
  hasOutstanding?: boolean;
  // hasNegativeBalance mirrors the dunning sweep's eligibility predicate
  // (dunning_excluded=FALSE, oldest overdue past the first-stage offset, net
  // outstanding above the org dust threshold) so the billing-dashboard
  // "Negative Accounts" surfaces agree with the dunning cockpit.
  hasNegativeBalance?: boolean;
  search?: string;
  // Server-side sort column + direction (Phase 6.2). Included in the query key
  // below so each sort caches independently (a re-sort doesn't clobber the
  // previous page's cache and vice-versa).
  sort?: string;
  order?: string;
  // Server-side lifecycle filter: "current" (active debtor accounts — a subsidy
  // office, or a family/contact with at least one child on a current/upcoming
  // contract), "left" (departed), or "all" / undefined (no restriction).
  // Composes with the has-credit/outstanding/negative filters and search.
  lifecycle?: "current" | "left" | "all";
  // Server-side aging bucket (days overdue as of today, the buckets of the
  // aggregates endpoint); "overdue" is any past-due bucket (#2175 D2).
  agingBucket?: DebtorAgingBucket;
  limit?: number;
  offset?: number;
  // Batch payloads for the page's debtors in the same response (#2194 A5):
  // "aggregates" and/or "series", comma-separated.
  include?: string;
}

export type DebtorAgingBucket = "current" | "d1_30" | "d31_60" | "d61_90" | "d90plus" | "overdue";

// debtorAccountKeys centralises the TanStack Query key namespace so the
// list / detail hooks + mutation invalidations stay in sync. Mirrors the
// `matchingRuleKeys` factory shape for consistency.
export const debtorAccountKeys = {
  all: ["reconciliation", "debtor-accounts"] as const,
  // list is scoped by the active orgId for the same reason summary/aggregates
  // are: a Clerk org switch does not clear the query cache, so without the org
  // dimension a cached list page could surface another org's debtors during
  // the staleTime window.
  list: (orgId: string | null | undefined, filters: DebtorAccountsListFilters) =>
    [...debtorAccountKeys.all, orgId ?? null, "list", filters] as const,
  detail: (id: string) => [...debtorAccountKeys.all, "detail", id] as const,
  // summary + aggregates are scoped by the active orgId: a Clerk org switch
  // does not clear the query cache, so without the org dimension a cached
  // payload could surface another org's portfolio figures during the
  // staleTime window.
  summary: (orgId: string | null | undefined) =>
    [...debtorAccountKeys.all, orgId ?? null, "summary"] as const,
  // The aggregates cache key includes the sorted ID list so two pages
  // with the same IDs (e.g. revisiting page 1 after page 2) share the
  // cached payload, while different pages don't collide.
  aggregates: (orgId: string | null | undefined, ids: readonly string[]) =>
    [
      ...debtorAccountKeys.all,
      orgId ?? null,
      "aggregates",
      [...ids].sort(),
    ] as const,
  // creditBatch powers the invoice-card credit pills (one batched read per
  // page chunk). Lives under `all` so recon money mutations that invalidate
  // debtorAccountKeys.all also refresh the pills — same freshness the old
  // per-card useDebtorAccount had (it shared this namespace too).
  creditBatch: (orgId: string | null | undefined, ids: readonly string[]) =>
    [
      ...debtorAccountKeys.all,
      orgId ?? null,
      "credit-batch",
      [...ids].sort(),
    ] as const,
};

export function useDebtorAccountsList(
  filters: DebtorAccountsListFilters = {},
) {
  const { orgId } = useAuth();
  return useQuery<reconciliation_DebtorAccountListResponse>({
    queryKey: debtorAccountKeys.list(orgId, filters),
    queryFn: () =>
      ReconciliationService.getApiV1ReconciliationDebtorAccounts(
        filters.hasCredit,
        filters.hasOutstanding,
        filters.hasNegativeBalance,
        filters.search,
        filters.sort,
        filters.order,
        filters.lifecycle,
        filters.limit,
        filters.offset,
        filters.agingBucket,
        filters.include,
      ),
    // Match the aggregates' staleness window so the list + money cells stay
    // coherent within a single page view and don't independently refetch.
    staleTime: 30_000,
    // Opt in — see the FRESHNESS POLICY note at the top of this file.
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
  });
}

/**
 * useDebtorAccountAggregates fetches the per-debtor monetary aggregates
 * for the visible page's IDs. Fires AFTER the cheap useDebtorAccountsList
 * call lands — the FE renders the names + open-invoice-count = "—"
 * immediately, then hydrates the money cells when this query resolves.
 *
 * `enabled` defaults to true when ids is non-empty; passing an empty
 * array short-circuits the query so we don't burn a network call when
 * the list is still loading or returned zero rows.
 *
 * Per-debtor outstanding values change every time a payment lands, so the
 * 30s staleness window is short and the query refetches on window focus (see
 * the FRESHNESS POLICY note at the top of this file).
 */
export function useDebtorAccountAggregates(ids: readonly string[]) {
  const { orgId } = useAuth();
  return useQuery<reconciliation_DebtorAccountAggregatesResponse>({
    queryKey: debtorAccountKeys.aggregates(orgId, ids),
    queryFn: () =>
      ReconciliationService.getApiV1ReconciliationDebtorAccountsAggregates(
        ids.join(","),
      ),
    enabled: ids.length > 0,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
  });
}

/**
 * useDebtorAccountsSummary fetches the org-wide KPI rollup
 * (Total outstanding / Unapplied credit / Net balance / debtor count)
 * that powers the summary-cards strip at the top of the Debtor Accounts
 * page. Aggregates ignore the operator's filter/search — these are
 * always the org total so the daycare owner sees the portfolio status
 * at a glance.
 */
export function useDebtorAccountsSummary(options?: { enabled?: boolean }) {
  const { orgId } = useAuth();
  return useQuery<reconciliation_DebtorAccountsSummaryResponse>({
    queryKey: debtorAccountKeys.summary(orgId),
    queryFn: () =>
      ReconciliationService.getApiV1ReconciliationDebtorAccountsSummary(),
    // 5min staleness — the rollup is portfolio-level and tolerates lag after a
    // payment lands. Mutation invalidations refresh it on demand in this tab,
    // and focus refetch (below) covers the writes that never reach this
    // QueryClient, which is what the window actually needed.
    //
    // The window stays at 5 minutes on purpose. This query is one of the two
    // org-wide aggregates that scan the full non-deleted invoices /
    // invoice_splits history on every call, against a 0.5 CPU Render tier that
    // throttles. Shortening it is a LOAD change, and nobody has measured that
    // endpoint at production scale yet (#987 Task 4). Revisit with the
    // EXPLAIN (ANALYZE, BUFFERS) numbers in hand, not before.
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
    enabled: options?.enabled ?? true,
  });
}
